import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
// import jwt_decode from "jwt-decode";

export const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  
    if (!email || !password) {
      toast({
        title: 'Missing Credentials',
        description: 'Please enter both email and password.',
        variant: 'destructive',
      });
      return;
    }
  
    setIsLoading(true);
    setErrorMessage('');
  
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
  
      const result = await res.json();

      console.log("Result :: " ,result)
  
      if (!res.ok) throw new Error(result.error || 'Login failed');
  
      // ✅ Save token and user in localStorage
      sessionStorage.setItem('auth_token', result.token);  // <-- token for future authenticated requests
      localStorage.setItem('user_session', JSON.stringify(result.user));
      
      toast({
        title: 'Login Successful',
        description: `Welcome back, ${result.user.first_name || result.user.email}`,
      });
  
      let redirectPath = '/';
      switch (result.user.role) {
        case 'admin': redirectPath = '/admin'; break;
        case 'doctor': redirectPath = '/doctor'; break;
        case 'patient': redirectPath = '/patient'; break;
        case 'staff': redirectPath = '/staff'; break;
        case 'hospital': redirectPath = '/hospital'; break;
        case 'insurance': redirectPath = '/insurance'; break;
      }
  
      navigate(redirectPath, { replace: true });
  
    } catch (err: any) {
      setErrorMessage(err.message);
      toast({
        title: 'Login Failed',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full max-w-md mx-auto">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
          required
        />
      </div>

      {errorMessage && (
        <div className="text-sm text-red-500 mt-2">{errorMessage}</div>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <span className="flex items-center justify-center">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Logging in...
          </span>
        ) : (
          'Log in'
        )}
      </Button>
    </form>
  );
};
